import React from 'react';
import { motion } from 'framer-motion';
import { Brain, Database, Workflow, Building2, Lightbulb, Heart, Sparkles } from 'lucide-react';
import { Card, CardContent } from '../ui/card';

const expertiseAreas = [
  {
    icon: Brain,
    title: 'Enterprise AI Strategy',
    description: 'Designing comprehensive AI adoption frameworks and transformation roadmaps for organizations.',
  },
  {
    icon: Database,
    title: 'AI Architecture & Platform Design',
    description: 'Building scalable AI platforms with enterprise LLM integration and orchestration frameworks.',
  },
  {
    icon: Workflow,
    title: 'Agent-Based Automation',
    description: 'Developing intelligent agent systems for enterprise workflow automation.',
  },
  {
    icon: Building2,
    title: 'Data Platform Modernization',
    description: 'Transforming legacy data systems into AI-ready modern architectures.',
  },
  {
    icon: Lightbulb,
    title: 'AI-Enabled Product Development',
    description: 'Creating innovative products that leverage AI capabilities.',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export function AboutSection() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-20 lg:py-32 relative"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-teal font-mono text-sm uppercase tracking-widest mb-4">
            My Story
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-6">
            Who I Am
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-teal to-orange mx-auto rounded-full" />
        </motion.div>

        {/* Personal Story Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <Card className="glass-light border-0 overflow-hidden">
            <CardContent className="p-8 lg:p-12">
              <div className="grid lg:grid-cols-5 gap-8 items-center">
                {/* Journey Icon */}
                <div className="lg:col-span-1 flex justify-center">
                  <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-teal/20 to-orange/20 flex items-center justify-center">
                    <Heart className="w-12 h-12 text-teal" strokeWidth={1.5} />
                  </div>
                </div>

                {/* Story Content */}
                <div className="lg:col-span-4 space-y-6">
                  <h3 className="text-2xl font-heading font-bold text-foreground">
                    A Journey Shaped by Adversity
                  </h3>
                  
                  <div className="space-y-4 text-foreground/90">
                    <p className="text-lg leading-relaxed">
                      Life taught me early that <span className="text-teal font-semibold">challenges are meant 
                      to be solved</span>. After facing major loss at a young age, I learned resilience isn't 
                      just about surviving — it's about building forward.
                    </p>
                    
                    <p className="text-base leading-relaxed text-muted-foreground">
                      I graduated high school early, driven by a need to make an impact. College followed, 
                      then <span className="text-foreground font-medium">service in the military</span> — 
                      experiences that instilled discipline, leadership, and a deep respect for teamwork.
                    </p>
                    
                    <p className="text-base leading-relaxed text-muted-foreground">
                      Growing up, I was raised with a simple philosophy: <span className="text-orange font-semibold italic">
                      "See a problem? Build a solution."</span> That mindset has shaped every step of my career 
                      and continues to drive everything I do today.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* AI Discovery Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <Card className="glass-light border-0 overflow-hidden border-l-4 border-l-teal">
            <CardContent className="p-8 lg:p-10">
              <div className="flex items-start gap-6">
                <div className="p-4 rounded-2xl bg-teal/10 shrink-0 hidden sm:flex">
                  <Sparkles className="w-8 h-8 text-teal" strokeWidth={1.5} />
                </div>
                <div className="space-y-4">
                  <h3 className="text-xl font-heading font-bold text-foreground">
                    Discovering AI Before the World Did
                  </h3>
                  <p className="text-base leading-relaxed text-muted-foreground">
                    In <span className="text-teal font-semibold">2022</span>, during a consulting engagement, 
                    I encountered GPT-2 and immediately recognized what most hadn't yet seen — 
                    <span className="text-foreground font-medium"> the next internet revolution was coming</span>. 
                    That moment changed everything.
                  </p>
                  <p className="text-base leading-relaxed text-muted-foreground">
                    Since then, I've dedicated my work to <span className="text-orange font-semibold">harnessing 
                    the power of AI to help people</span> — not replace them. My focus is on the 
                    <span className="text-foreground font-medium"> Human + AI perspective</span>: 
                    How can AI help us be better? How can AI help us deliver things for people we 
                    couldn't deliver before? How do we build technology that empowers rather than diminishes?
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Main Content - Expertise */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-stretch">
          {/* Left - Professional Summary */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="h-full"
          >
            <Card className="glass-light border-0 overflow-hidden h-full">
              <CardContent className="p-8 lg:p-10 h-full flex flex-col">
                <p className="text-sm font-mono text-teal uppercase tracking-wider mb-4">
                  Professional Background
                </p>
                <div className="space-y-6 text-foreground/90 flex-grow">
                  <p className="text-lg leading-relaxed font-body">
                    Today, I'm an <span className="text-teal font-semibold">enterprise technology 
                    and AI transformation leader</span> with deep experience across consulting, 
                    digital product development, and enterprise platform architecture.
                  </p>
                  <p className="text-base leading-relaxed text-muted-foreground font-body">
                    My work spans industries including <span className="text-foreground">financial services, 
                    healthcare, telecommunications, retail, and government modernization</span>.
                  </p>
                  <p className="text-base leading-relaxed text-muted-foreground font-body">
                    I help organizations <span className="text-orange font-medium">adopt and operationalize 
                    artificial intelligence</span> through modern data platforms, AI architecture, 
                    and enterprise AI operating models — always with humans at the center.
                  </p>
                </div>

                {/* Quote */}
                <div className="mt-8 pt-8 border-t border-border">
                  <p className="text-sm text-muted-foreground font-mono italic">
                    "AI should amplify human potential, not replace human purpose."
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Right - Expertise Cards */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="flex flex-col h-full"
          >
            <p className="text-sm font-mono text-teal uppercase tracking-wider mb-4">
              Core Areas of Expertise
            </p>
            
            <div className="space-y-3 flex-grow flex flex-col">
              {expertiseAreas.map((area, index) => (
                <motion.div
                  key={area.title}
                  variants={itemVariants}
                  whileHover={{ x: 8, transition: { duration: 0.2 } }}
                  className="flex-1"
                >
                  <Card 
                    className="glass-light border-0 hover:border-teal/30 transition-all duration-300 cursor-default h-full"
                    data-testid={`expertise-card-${index}`}
                  >
                    <CardContent className="p-4 flex items-center gap-4 h-full">
                      <div className="p-3 rounded-xl bg-teal/10 text-teal shrink-0">
                        <area.icon className="w-5 h-5" strokeWidth={1.5} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-heading font-semibold text-foreground mb-1">
                          {area.title}
                        </h3>
                        <p className="text-sm text-muted-foreground font-body leading-snug">
                          {area.description}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
